import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import type { PersonImportStrategyExecutorResult } from '../api/PersonImportAPI.js';
import { PersonImportStrategyDataTable } from './PersonImportStrategyDataTable.js';

// module-level so the reference is stable across re-renders - the table memoizes its options on it
const RESULT_TABLE_FIELDS = [
  new FieldConfiguration({ customID: 'person' }),
  new FieldConfiguration({ customID: 'changes' }),
  new FieldConfiguration({ fieldName: 'error' }),
  new FieldConfiguration({ customID: 'actions' }),
];

const PersonImportResultDialog = stateful(
  ({
    resolve,
    reject,
    processId,
    result,
  }: {
    resolve: () => void;
    reject: () => void;
    processId: string;
    result?: PersonImportStrategyExecutorResult;
  }) => {
    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: 'Import abgeschlossen',
          subtitle: 'Der Import wurde abgeschlossen. Sie können hier die Ergebnisse einsehen.',
          close: reject,
          closeLabel: 'Dialog schließen',
        })}
        <div class="border-base-300 bg-base-200/40 px-6 py-4 rounded-box">
          <div class="flex flex-wrap gap-3">
            <div
              class="badge badge-success badge-lg border-none px-4 py-4 text-base font-semibold text-success-content"
            >
              ${result?.entriesInserted ?? 0} neu
            </div>
            <div
              class="badge badge-primary badge-lg border-none px-4 py-4 text-base font-semibold text-primary-content"
            >
              ${result?.entriesUpdated ?? 0} aktualisiert
            </div>
          </div>
        </div>
        <div>
          <h3 class="font-semibold">Fehlerhafte Imports</h3>
          <p class="text-sm text-base-content/70">Hier können Sie die fehlerhaften Imports einsehen und bearbeiten.</p>
        </div>
        <div class="overflow-y-auto h-[50vh]">
          ${PersonImportStrategyDataTable({
            processId,
            fields: RESULT_TABLE_FIELDS,
          })}
        </div>
        <div class="sticky bottom-0 z-10 border-t border-base-300 bg-base-100/95 backdrop-blur-sm px-6 py-4">
          <div class="flex justify-end">
            <button class="btn btn-primary" @click=${() => reject()}>Schließen</button>
          </div>
        </div>
      </div>
    `;
  },
);

export async function openPersonImportResultDialog(
  processId: string,
  result?: PersonImportStrategyExecutorResult,
): Promise<void> {
  return showDialog({
    content: (resolve, reject) => PersonImportResultDialog({ resolve, reject, processId, result }),
    modalBoxClasses: 'w-[95vw] h-[95vh] max-w-[95vw] max-h-[95vh]',
  });
}
